import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Avatar, ListItem, ListItemAvatar, ListItemText, makeStyles, Theme } from '@material-ui/core';
import { useTranslation } from '@fluentsms/i18n';
import { MentionComponent } from '@fluentsms/syncfusion';

export interface MentionableUser {
  id: string;
  name: string;
  email: string;
  avatar: string;
  isOnOrder: boolean;
  staffRole?: string[];
}

export interface MentionableProps {
  target: string;
  orderId?: string;
  users: MentionableUser[];
  status: 'loading' | 'loaded';
  onMentionUser?(userId: string): void;
  onUnmentionUser?(userId: string): void;
}

export interface UserItemProps {
  avatar: string;
  name: string;
  email: string;
}

const NAME_ID_SEPARATOR = '__';

export function Mentionable({ target, orderId, users, status, onMentionUser, onUnmentionUser }: MentionableProps) {
  const { t } = useTranslation('componentLibrary');
  const initiallyLoaded = useRef(false);
  const mentionRef = useRef<null>(null);
  const userLookup = users.reduce((prev, next) => {
    prev[next.id] = next;
    return prev;
  }, {} as Record<string, MentionableUser>);
  const dataSource = users.map((user) => ({
    ...user,
    name: [user.name, user.id].join(NAME_ID_SEPARATOR),
    orderStatus: user.isOnOrder ? t('mentionable.onThisOrder') : t('mentionable.notOnThisOrder'),
  }));
  const template = useCallback(
    (item: MentionableUser) => <UserItem avatar={item.avatar} name={item.name} email={item.email} />,
    [],
  );

  /**
   * When `MentionableComponent` transitions from a "loading" state to a "loaded" state,
   * if the selection popup is open, it will not automatically transition into showing the names until the next time the user types.
   * To avoid situations where a user opens an editor, types "@" and sees "Loading..." and it never changes,
   * we need to manually update the popup component with the newly loaded items.
   * Unfortunately, the default behavior of MentionComponent#addItem will add empty <li> elements to the popup. Luckily, they still include the
   * user's ID attached to the `data-value` attribute. We can then grab the associated user and manually recreate the <UserItem /> component's
   * look and feel.
   */
  useEffect(() => {
    if (!initiallyLoaded.current && status === 'loaded') {
      initiallyLoaded.current = true;
      (mentionRef.current as any)?.addItem(dataSource);

      setTimeout(() => {
        const listItems = Array.from(document.querySelectorAll('.e-list-item'));

        for (const item of listItems) {
          const userId = item.getAttribute('data-value');
          const user = userLookup[userId ?? ''];

          if (user) {
            item.innerHTML = manuallyCreateUserItem(user);
          }
        }
      }, 0);
    }
  });

  /**
   * syncfusion doesn't seem to provide a way to listen for removal of mentions,
   * so something like this is necessary.
   */
  useLayoutEffect(() => {
    const editor = document.querySelector('.e-content');
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const removedNodes = Array.from(mutation.removedNodes);

        for (const node of removedNodes) {
          if ((node as { classList?: NodeList }).classList) {
            const element = node as Element;

            if (element.classList.contains('e-mention-chip')) {
              const userId = element.getAttribute('data-userid');

              if (userId) {
                onUnmentionUser?.(userId);
              }
            }
          }
        }
      }
    });

    if (editor) {
      observer.observe(editor, { childList: true, subtree: true });

      return () => {
        observer.disconnect();
      };
    }
  });

  return (
    <MentionComponent
      ref={mentionRef}
      target={target}
      dataSource={dataSource}
      showMentionChar={true}
      allowSpaces={true}
      fields={{
        text: 'name',
        value: 'id',
        groupBy: orderId ? 'orderStatus' : undefined,
      }}
      itemTemplate={template}
      noRecordsTemplate={status === 'loading' ? t('mentionable.loading') : t('mentionable.noOptions')}
      change={(event) => {
        applyTitleAndLink(event, users);
        onMentionUser?.(event.itemData.id);
      }}
    />
  );
}

const useUserItemStyles = makeStyles((theme: Theme) => ({
  listItem: {
    textIndent: 0,
  },
  avatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    color: theme.palette.text.secondary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '2px solid transparent',
    backgroundColor: theme.palette.grey[200],
    '& svg': {
      color: theme.palette.secondary.main,
    },
    '&:hover': {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

function UserItem({ avatar, name: nameWithId, email }: UserItemProps) {
  const styles = useUserItemStyles();
  const [name] = nameWithId.split(NAME_ID_SEPARATOR);
  const [firstName = '', lastName = ''] = name.split(' ');
  const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();

  return (
    <ListItem className={styles.listItem}>
      <ListItemAvatar>
        <Avatar className={styles.avatar} src={avatar}>
          <div>{initials}</div>
        </Avatar>
      </ListItemAvatar>
      <ListItemText primary={name} secondary={email} />
    </ListItem>
  );
}

// See: https://stackoverflow.com/questions/13860417/can-i-change-an-html-elements-type/65090521#65090521
function changeTag(node: Element, tag: string) {
  const clone = createElement(tag);
  for (const attr of Array.from(node.attributes)) {
    clone.setAttributeNS(null, attr.name, attr.value);
  }
  while (node.firstChild) {
    clone.appendChild(node.firstChild);
  }

  node.replaceWith(clone);

  // Move the user's cursor to the end.
  const editor = clone.parentElement?.parentElement;

  if (editor) {
    setEndOfContenteditable(editor);
  }

  return clone;
}

function createElement(tag: string) {
  if (tag === 'svg') {
    return document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  } else {
    return document.createElementNS('http://www.w3.org/1999/xhtml', tag);
  }
}

/**
 * By default, MentionComponent adds the selected entry as a span.
 * We need to transform it into an <a /> element instead, and adjust the title and behavior
 * to open a new tab in the user's mail client of choice.
 */
function applyTitleAndLink(event: { element: Element }, users: MentionableUser[]) {
  const userLookup = users.reduce((prev, next) => {
    prev[next.id] = next;
    return prev;
  }, {} as Record<string, MentionableUser>);
  const newMentions = Array.from(event.element.querySelectorAll('span.e-mention-chip'));

  for (const mention of newMentions) {
    const nameWithId = mention.innerHTML.replace('@', ''); // Turns @Connor Bryan__cl7gqq6oh0000pvowc4fa3lae into Connor Bryan__cl7gqq6oh0000pvowc4fa3lae
    const [name, id] = nameWithId.split(NAME_ID_SEPARATOR);
    const email = userLookup[id]?.email ?? '';

    // Remove the extraneous __<id> from the actual displayed mention
    mention.innerHTML = `@${name}`;

    mention.setAttribute('data-userid', id);
    mention.setAttribute('title', email);
    mention.setAttribute('href', `mailto:${email}`);
    mention.setAttribute('target', '_blank');
    mention.setAttribute('rel', 'noopener noreferrer');

    changeTag(mention, 'a');
  }
}

/**
 * This function recreates the style output of <UserListItem /> via inline-styles, since the ClassNameMap from `useUserItemStyles()`
 * does not work in this context.
 */
function manuallyCreateUserItem(user: MentionableUser) {
  const [firstName = '', lastName = ''] = user.name.split(' ');
  const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
  const avatarListItemRootStyle = `
    min-width: 64px;
    flex-shrink: 0;
    padding-right: 16px;
  `;
  const avatarRootStyle = `
    width: 48px;
    height: 48px;
    color: #677784;
    background-color: #eeeeee;
    border-radius: 50%;
    border: 2px solid transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
    font-size: 1.25rem;
    line-height: 1;
    user-select: none;
  `;
  const listItemRootStyle = `
    width: 100%;
    display: flex;
    position: relative;
    box-sizing: border-box;
    text-align: left;
    align-items: center;
    padding-top: 8px;
    padding-left: 24px;
    padding-bottom: 8px;
    justify-content: flex-start;
    text-decoration: none;
    padding-left: 16px;
    padding-right: 16px;
    text-indent: 0;
  `;
  const listItemTextStyle = `
    margin-top: 2px;
    margin-bottom: 2px;
    flex: 1 1 auto;
    min-width: 0;
    text-indent: 0;
  `;
  const nameStyle = `
    display: block;
    color: #263238;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: 0.15px;
    margin: 0;
  `;
  const emailStyle = `
    color: #677784;
    display: block;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.43;
    letter-spacing: 0.15px;
    margin: 0;
  `;

  return `
    <li style="${listItemRootStyle}">
      <div style="${avatarListItemRootStyle}">
        <div style="${avatarRootStyle}">
          <div>${initials}</div>
        </div>
      </div>
      <div style="${listItemTextStyle}">
        <span style="${nameStyle}">${user.name}</span>
        <p style="${emailStyle}">${user.email}</p>
      </div>
    </li>
  `;
}

// See: https://stackoverflow.com/questions/1125292/how-to-move-cursor-to-end-of-contenteditable-entity
function setEndOfContenteditable(contentEditableElement: Element) {
  let range: Range, selection: Selection | null;

  if (document.createRange) {
    //Firefox, Chrome, Opera, Safari, IE 9+
    range = document.createRange(); //Create a range (a range is a like the selection but invisible)
    range.selectNodeContents(contentEditableElement); //Select the entire contents of the element with the range
    range.collapse(false); //collapse the range to the end point. false means collapse to end rather than the start
    selection = window.getSelection(); //get the selection object (allows you to change selection)
    selection?.removeAllRanges(); //remove any selections already made
    selection?.addRange(range); //make the range you have just created the visible selection
  }
}
